import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Mail } from "lucide-react";

const PrivacyPolicy = () => {
  const navigate = useNavigate();
  const lastUpdated = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      {/* Header */}
      <header className="glass-effect border-b border-white/20">
        <div className="container mx-auto px-6 py-3">
          <div className="flex items-center gap-4">
            <Button
              onClick={() => navigate('/')}
              variant="ghost"
              size="sm"
              className="hover:bg-white/50"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Button>
            <div className="flex items-center gap-3">
              <img 
                src="/images/robot-genie-logo.png" 
                alt="Kids Genie" 
                className="w-12 h-12 object-contain"
              />
              <div>
                <span className="text-xl font-bold bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 bg-clip-text text-transparent">
                  Kids Genie AI, LLC
                </span>
                <div className="text-xs font-medium text-gray-600 -mt-1">AI & Coding Academy</div>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto max-w-4xl px-6 py-12">
        <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">
              Privacy Policy
            </h1>
            <p className="text-sm text-gray-600">Last Updated: {lastUpdated}</p>
          </div>

          {/* Contact Box */}
          <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-4 mb-8">
            <div className="flex items-start gap-3">
              <Mail className="w-5 h-5 text-indigo-600 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-gray-900 mb-1">
                  Questions about privacy?
                </p>
                <a 
                  href="mailto:support@kidsgenie-ai-academy.com" 
                  className="text-sm text-indigo-600 hover:underline"
                >
                  support@kidsgenie-ai-academy.com
                </a>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="prose max-w-none">
            <p className="text-gray-700 leading-relaxed mb-6">
              Kids Genie AI LLC ("we", "our", or "us") is committed to protecting the privacy of both children and adults who use our platform. This Privacy Policy explains what information we collect, how we use it, and how we keep it safe. Our website and digital learning tools are designed for children, but parents and guardians are always in control of accounts and payments.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">1. Who We Are</h2>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li><strong>Company Name:</strong> Kids Genie AI LLC</li>
              <li><strong>Website:</strong> <a href="https://kidsgenie-ai-academy.com" className="text-indigo-600 hover:underline">https://kidsgenie-ai-academy.com</a></li>
              <li><strong>Country:</strong> United States (Delaware LLC), operating globally</li>
              <li><strong>Owner:</strong> Mohamad Sami Abbas</li>
            </ul>
            <p className="text-gray-700 mt-4">
              We provide online learning experiences that help children learn coding, creativity, and problem-solving using interactive and AI-assisted lessons.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">2. Who This Policy Applies To</h2>
            <p className="text-gray-700 mb-4">This Privacy Policy applies to:</p>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li>Children using the platform for learning</li>
              <li>Parents or guardians who create and manage accounts</li>
              <li>Visitors browsing our website</li>
            </ul>
            <p className="text-gray-700 mt-4 font-semibold">
              Children under age 13 can only use the platform with parent or guardian consent. This is required by the Children's Online Privacy Protection Act (COPPA).
            </p>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4">
              <p className="text-sm font-semibold text-blue-900 mb-2">Parent Registration Process:</p>
              <p className="text-sm text-blue-800 mb-2">
                During registration, parents/guardians must:
              </p>
              <ul className="list-disc pl-6 space-y-1 text-sm text-blue-800">
                <li>Confirm they are <strong>18 years or older</strong></li>
                <li>Confirm they are the <strong>legal guardian</strong> of the child</li>
                <li>Provide their child's <strong>first name</strong> and <strong>age (6-12 years)</strong></li>
                <li>Accept our <strong>Terms of Service</strong> and <strong>Privacy Policy</strong></li>
              </ul>
              <p className="text-sm text-blue-800 mt-2">
                This process ensures we comply with COPPA and GDPR-K regulations while providing a safe learning environment for children.
              </p>
            </div>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">3. Information We Collect</h2>
            
            <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">Information Provided by Parents</h3>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li>Parent/guardian name</li>
              <li>Email address</li>
              <li>Payment information (handled securely by Stripe or PayPal — we do not store card data)</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">Information About the Child (Only with Parent Permission)</h3>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li>First name (or nickname)</li>
              <li>Age or age range</li>
              <li>Learning progress and lessons completed</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">Automatically Collected Information (For Platform Function Only)</h3>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li>Device type (phone, tablet, computer)</li>
              <li>Browser type</li>
              <li>General region/country (not precise location)</li>
            </ul>

            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mt-4">
              <p className="text-sm font-semibold text-green-900 mb-2">We do NOT collect:</p>
              <ul className="list-disc pl-6 space-y-1 text-sm text-green-800">
                <li>Precise GPS location</li>
                <li>Personal contact information for children</li>
                <li>Government ID numbers</li>
              </ul>
            </div>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">4. How We Use This Information</h2>
            <p className="text-gray-700 mb-4">We use information to:</p>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li>Create and manage accounts</li>
              <li>Personalize learning lessons</li>
              <li>Track progress and achievements</li>
              <li>Improve platform experience</li>
              <li>Communicate with parents</li>
            </ul>
            <p className="text-gray-700 mt-4 font-semibold">
              We do not sell personal data. We do not share it for advertising.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">5. Who We Share Information With</h2>
            <p className="text-gray-700 mb-4">
              We only share necessary data with trusted service providers, such as:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li>Stripe / PayPal (for secure payments)</li>
              <li>Platform hosting services (to run the website)</li>
            </ul>
            <p className="text-gray-700 mt-4">
              These companies must follow strict privacy and security rules. We never sell or trade personal information.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">6. Parental Rights and Controls</h2>
            <p className="text-gray-700 mb-4">Parents can at any time:</p>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li>View their child's learning records</li>
              <li>Update or correct account details</li>
              <li>Request deletion of their child's data</li>
            </ul>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4">
              <p className="text-sm text-blue-900">
                <strong>To request deletion:</strong> Email{" "}
                <a href="mailto:support@kidsgenie-ai-academy.com" className="text-blue-600 hover:underline">
                  support@kidsgenie-ai-academy.com
                </a>
              </p>
              <p className="text-xs text-blue-800 mt-2">
                If data is deleted, the child's progress and account access may be removed.
              </p>
            </div>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">7. International Users</h2>
            <p className="text-gray-700 mb-4">We follow privacy requirements worldwide, including:</p>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li>GDPR (Europe)</li>
              <li>COPPA (United States)</li>
              <li>Regional data protection laws where applicable</li>
            </ul>
            <p className="text-gray-700 mt-4">
              Your data may be stored on secure servers in the United States or other regions depending on system performance.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">8. Security</h2>
            <p className="text-gray-700">
              We use industry-standard encryption and security practices to keep personal information safe. However, no online system is 100% secure. If we ever experience a data issue, we will notify affected users promptly.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">9. Changes to This Policy</h2>
            <p className="text-gray-700">
              We may update this policy occasionally to improve clarity or meet legal requirements. When we do, we will post the updated date at the top.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">10. Contact Us</h2>
            <p className="text-gray-700">
              If you have questions or would like to exercise any privacy rights, email us at:{" "}
              <a href="mailto:support@kidsgenie-ai-academy.com" className="text-indigo-600 hover:underline font-semibold">
                support@kidsgenie-ai-academy.com
              </a>
            </p>
            <p className="text-gray-700 mt-2 font-medium">We are here to help.</p>
          </div>

          {/* Footer Actions */}
          <div className="mt-12 pt-8 border-t flex flex-wrap gap-4">
            <Button
              onClick={() => window.print()}
              variant="outline"
              className="rounded-full"
            >
              Print This Policy
            </Button>
            <Button
              onClick={() => navigate('/terms')}
              variant="outline"
              className="rounded-full"
            >
              View Terms of Service
            </Button>
            <Button
              onClick={() => navigate('/contact')}
              className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-full"
            >
              Contact Support
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
};

export default PrivacyPolicy;